import React, { useEffect, useState } from "react";
import Navbar from "../NavBar/Navbar";
import FadeLoader from "react-spinners/FadeLoader";
import { useUser } from "../utils/UserContext.jsx";
import "./Earnings.css";
import trophyIcon from "../../Images/Trophy.png";
import trendUpIcon from "../../Images/TrendUp.png";
import walletIcon from "../../Images/Wallet.png";

// 🔢 Centralize numeric length rules here
const NUMBER_RULES = {
  accountNumber: { min: 9, max: 18, label: "Account number" },
  routingNumber: { len: 9, label: "Routing number" },
};

const Earnings = () => {
  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

  const [showCashoutModal, setShowCashoutModal] = useState(false);
  const [topEarnings, setTopEarnings] = useState(null);
  const [activeProps, setActiveProps] = useState(null);
  const [completedCashouts, setCompletedCashouts] = useState([]);
  const [allCashouts, setAllCashouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [showAll, setShowAll] = useState(false);
  const [entityId, setEntityId] = useState(null);
  const { userDetails } = useUser();


  const [cashoutData, setCashoutData] = useState({
    amount: "",
    payoutMethod: "Bank Transfer",
    accountHolder: "",
    accountNumber: "",
    routingNumber: "",
    bankName: ""
  });
  const resetCashoutForm = () => {
  setCashoutData({
    amount: "",
    payoutMethod: "Bank Transfer",
    accountHolder: "",
    accountNumber: "",
    routingNumber: "",
    bankName: ""
  });
  setErrors({});
};

 
  // ✅ Helper Functions (add these ABOVE handleInputChange)
const onlyDigits = (value) => value.replace(/\D/g, "");

const onlyLettersAndSpaces = (value) =>
  value.replace(/[^A-Za-z ]/g, "").replace(/\s{2,}/g, " ");

const sanitizeAmount = (value) => {
  let val = value.replace(/[^\d.]/g, "");
  const parts = val.split(".");
  if (parts.length > 2) val = parts[0] + "." + parts.slice(1).join("");
  const [intPart, decimal = ""] = val.split(".");
  return decimal ? `${intPart}.${decimal.slice(0, 2)}` : intPart;
};

// ✅ Corrected input handler
const handleInputChange = (e) => {
  const { name, value } = e.target;

  if (name === "accountNumber") {
    const max = NUMBER_RULES.accountNumber.max;
    return setCashoutData((p) => ({
      ...p,
      accountNumber: onlyDigits(value).slice(0, max),
    }));
  }

  if (name === "routingNumber") {
    const max = NUMBER_RULES.routingNumber.len;
    return setCashoutData((p) => ({
      ...p,
      routingNumber: onlyDigits(value).slice(0, max),
    }));
  }

  if (name === "accountHolder") {
    return setCashoutData((p) => ({
      ...p,
      accountHolder: onlyLettersAndSpaces(value),
    }));
  }

  if (name === "bankName") {
    return setCashoutData((p) => ({
      ...p,
      bankName: onlyLettersAndSpaces(value),
    }));
  }

  if (name === "amount") {
    return setCashoutData((p) => ({
      ...p,
      amount: sanitizeAmount(value),
    }));
  }

  setCashoutData((prev) => ({ ...prev, [name]: value }));
};


 useEffect(() => {
  // ✅ Set entityId if userDetails loaded
  if (userDetails && !entityId) {
    setEntityId(userDetails.entityRegistrationID);
    return; 
  }

  // ✅ Only fetch when entityId is ready
  if (!entityId) return;

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // ✅ FETCH Top Earnings (with EntityRegistrationID)
      const earningsResponse = await fetch(`${hostPath}/api/GetTopEarnings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": hostHeader
        },
        body: JSON.stringify({ EntityRegistrationID: entityId })
      });

      if (!earningsResponse.ok) throw new Error("Failed to fetch top earnings");
      const earningsData = await earningsResponse.json();
      setTopEarnings(earningsData);

      // ✅ FETCH Active Properties + Balances
      const propsResponse = await fetch(`${hostPath}/api/GetActivePropsAndWithdrawals`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": hostHeader
        },
        body: JSON.stringify({ EntityRegistrationID: entityId })
      });

      if (!propsResponse.ok) throw new Error("Failed to fetch active properties");
      const propsData = await propsResponse.json();
      setActiveProps(propsData);

      // ✅ FETCH Cashout History
      const recentResponse = await fetch(`${hostPath}/api/GetRecentCashoutsHistoy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": hostHeader
        },
         body: JSON.stringify({ EntityRegistrationID: userDetails.entityRegistrationID })
      });
      if (!recentResponse.ok) throw new Error("Failed to fetch recent cashout history");
      const allrecentData = await recentResponse.json();
      setCompletedCashouts(allrecentData.completedCashouts || []);
      setAllCashouts(allrecentData.allCashouts || []);

    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  fetchData();

}, [entityId, userDetails, hostPath, hostHeader]);


  // ✅ VALIDATION FUNCTION
  const validateCashout = () => {
    let newErrors = {};

    if (!cashoutData.amount || Number(cashoutData.amount) <= 0) {
      newErrors.amount = "Enter a valid cashout amount.";
    } else if (Number(cashoutData.amount) < Number(activeProps?.minimumRequired)) {
      newErrors.amount = `Minimum cashout is $${activeProps?.minimumRequired}`;
    }

    if (!cashoutData.payoutMethod) {
      newErrors.payoutMethod = "Please select a payout method.";
    }

    if (!cashoutData.accountHolder.trim()) {
      newErrors.accountHolder = "Account holder name is required.";
    } else if (!/^[A-Za-z ]+$/.test(cashoutData.accountHolder.trim())) {
      newErrors.accountHolder = "Name should contain only letters.";
    }

    // Account Number (digits only + length)
    const acc = cashoutData.accountNumber.trim();
    const accLen = acc.length;
    if (!acc) {
      newErrors.accountNumber = "Account number is required.";
    } else if (!/^\d+$/.test(acc)) {
      newErrors.accountNumber = "Account number should contain only digits.";
    } else if (accLen < NUMBER_RULES.accountNumber.min || accLen > NUMBER_RULES.accountNumber.max) {
      newErrors.accountNumber = `${NUMBER_RULES.accountNumber.label} must be ${NUMBER_RULES.accountNumber.min}–${NUMBER_RULES.accountNumber.max} digits (you entered ${accLen}).`;
    }

    // Routing Number (digits only + exact length)
    const route = cashoutData.routingNumber.trim();
    const routeLen = route.length;
    if (!route) {
      newErrors.routingNumber = "Routing number is required.";
    } else if (!/^\d+$/.test(route)) {
      newErrors.routingNumber = "Routing number should contain only digits.";
    } else if (routeLen !== NUMBER_RULES.routingNumber.len) {
      newErrors.routingNumber = `${NUMBER_RULES.routingNumber.label} must be ${NUMBER_RULES.routingNumber.len} digits (you entered ${routeLen}).`;
    }

    if (!cashoutData.bankName.trim()) {
      newErrors.bankName = "Bank name is required.";
    } else if (!/^[A-Za-z ]+$/.test(cashoutData.bankName.trim())) {
      newErrors.bankName = "Bank name should contain only letters.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

      const refreshCashoutHistory = async () => {
      const recentResponse = await fetch(`${hostPath}/api/GetRecentCashoutsHistoy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": hostHeader
        },
        body: JSON.stringify({ EntityRegistrationID: userDetails.entityRegistrationID })
      });

      if (!recentResponse.ok) return;

      const data = await recentResponse.json();
      setCompletedCashouts(data.completedCashouts || []);
      setAllCashouts(data.allCashouts || []);
    };

  const handleSubmitCashout = async () => {
     if (!validateCashout()) return;

    const amount = Number(parseFloat(cashoutData.amount).toFixed(2));
    const payload = {
      EntityRegistrationID :userDetails.entityRegistrationID,
      CashoutAmount: Number(parseFloat(cashoutData.amount).toFixed(2)),
      PayoutMethod: cashoutData.payoutMethod,
      AccountHolderName: cashoutData.accountHolder,
      AccountNumber: cashoutData.accountNumber,
      RoutingNumber: cashoutData.routingNumber,
      BankName: cashoutData.bankName
    };

          try {
            const response = await fetch(`${hostPath}/api/CreateCashoutRequest`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "x-functions-key": hostHeader
              },
              body: JSON.stringify(payload)
            });

            if (!response.ok) throw new Error("Failed to submit cashout request");
            // ✅ Update UI immediately (subtract amount)
          setActiveProps((prev) => ({
            ...prev,
            withdrawableAmount: Number(prev.withdrawableAmount) - amount
          }));

          // ✅ Refresh cashout history
          await refreshCashoutHistory();
          alert("✅ Cashout request submitted successfully!");

          resetCashoutForm();
          setShowCashoutModal(false);

          } catch (error) {
            console.error("Error submitting cashout request:", error);
            alert("Something went wrong while submitting the request.");
          }
        };

  // ✅ DERIVED VALUES
  const totalRequests = completedCashouts.length;

  const lastCashoutDate =
    totalRequests > 0
      ? new Date(completedCashouts[0].date).toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        })
      : "No Records";

  if (error) return <div className="error">Error: {error}</div>;

  return (
    <>
      <Navbar />

      <div className="earnings-container">
        {loading ? (
          <div className="loader-overlay">
            <FadeLoader/>
          </div>
        ) : (
          <>
            <h2>Your Earnings Overview</h2>
            <p className="overview-subtitle">
              Track your property-wise revenue, combined balance, and manage your
              cashouts with ease.
            </p>

            <div className="cards-grid">
              {/* Top Earning Property */}
              <div className="card purple">
              <div className="icon-badge icon-box1">
                <img src={trophyIcon} alt="Trophy Icon" className="icon-img" />
              </div>
              <p>Top Earning Property</p>
              <strong>
                DownTown Parking Lot {topEarnings?.topEarningProperty? topEarnings.topEarningProperty.propertyNickName: ""}
              </strong>
              <div className="card-amount">
                <div className="amount-row">
                  <h2>
                    ${topEarnings?.topEarningProperty?.totalEarnings? topEarnings.topEarningProperty.totalEarnings.toFixed(2): "0.00"}
                  </h2>
                  <br />
                  <br />
                  <span className="month-text">this month</span>
                </div>
                <p className="percentage-text">
                  {topEarnings?.topEarningProperty?.percentage? topEarnings.topEarningProperty.percentage: 0}% of total earnings
                </p>
              </div>
            </div>

              {/* Total Earnings Card */}
                <div className="card blue">
  <div className="icon-badge icon-box2">
    <img src={trendUpIcon} alt="TrendUp Icon" className="icon-img" />
  </div>

  {/* Title */}
  <p>Total Earnings (All Properties)</p>
  <p>
    <strong>{activeProps?.totalPropertiesCount ?? 0} All Properties</strong>
  </p>
  <h2>${activeProps?.totalAmount_AllTime?.toFixed(2) ?? 0}</h2>
  <p>
    <strong>{activeProps?.totalPropertiesCount_CurrentMonth ?? 0} Active Properties this month</strong>
  </p>
   <div className="amount-row">
  <h2>${activeProps?.totalAmount_CurrentMonth?.toFixed(2) ?? 0}</h2>
  <span className="month-text">this month</span>
  </div>
    </div>


              {/* Available for Cashout */}
              <div className="card green">
                <div className="icon-badge icon-box3">
                    <img src={walletIcon} alt="Wallet Icon" className="icon-img" />
                </div>
                <p>Available for Cashout</p>
                <strong>Ready for Withdrawal</strong>
                <br></br>
                {/* <h2>${activeProps?.withdrawableAmount ?.toFixed(2) ?? 0}</h2> */}
                <h2>${Number(activeProps?.withdrawableAmount || 0).toFixed(2)}</h2>
                <p>
                  Minimum required: ${activeProps?.minimumRequired}
                </p>
              </div>

              {/* ✅ Recent Cashout History */}
              <div className="card wide">
                <div className="cashout-header">
                  <div className="cashout-title-block">
                    <h3 class="request-title">Recent Cashout History</h3>
                    <p>View your latest cashout transactions</p>
                  </div>
                  {(allCashouts.length > 4 || showAll) && (
                    <button
                      className="see-all-btn"
                      onClick={() => setShowAll(!showAll)}
                    >
                      {showAll ? "See less ←" : "See all →"}
                    </button>
                  )}
                </div>

               {completedCashouts.length > 0 ? (
              <div className="cashout-table-wrapper">
                <table className="cashout-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Payout Method</th>
                      <th>Status</th>
                      <th>Reference ID</th>
                    </tr>
                  </thead>
                </table>

                <div className="cashout-table-scroll">
                  <table className="cashout-table">
                    <tbody>
                      {(showAll ? allCashouts : completedCashouts.slice(0, 4)).map(
                        (item, index) => (
                          <tr key={index}>
                            <td>{item.date}</td>
                            <td>${item.amount}</td>
                            <td>{item.paymentMode}</td>
                            <td>
                              <span
                                className={`status-badge ${item.status.toLowerCase()}`}
                              >
                                {item.status}
                              </span>
                            </td>
                            <td>{item.referenceId ?? "N/A"}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="no-data">No cashout history found</p>
            )}
              </div>

              {/* Request Cashout */}
              <div className="card request-card">
                <h3 className="request-title">Request a Cashout</h3>
                <p className="request-sub">
                  You can request a payout once your balance reaches $25 or more.
                  Cashouts are reviewed and processed by the GeoParking finance team.
                </p>

                <div className="request-info-row">
                  <div className="info-box">
                    <p className="info-label">Last Cashout</p>
                    <p className="info-value">{lastCashoutDate}</p>
                  </div>

                  <div className="info-box">
                    <p className="info-label">Total Requests</p>
                    <p className="info-value">{totalRequests}</p>
                  </div>
                </div>
                
               <button
              className="request-btn"
              disabled={activeProps?.withdrawableAmount < activeProps?.minimumRequired}
              onClick={() => {
                if (activeProps?.withdrawableAmount >= activeProps?.minimumRequired) {
                  resetCashoutForm();
                  setShowCashoutModal(true);
                }
              }}
            >
              Request Cashout
            </button>
                <p className="note">
                  All requests are securely processed by our finance team within
                  3–5 business days.
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {showCashoutModal && (
  <div className="modal-overlay">
    <div className="modal-container">

      <div className="modal-header-text">
        <strong>Request Cashout</strong>
        <h3>
          Request a payout from your available balance. The GeoParking
          Finance team will review your request within 3–5 business days.
        </h3>
      </div>

      <div className="balance-box">
        <p>Available Balance</p>
        <h2>${activeProps?.withdrawableAmount}</h2>
        <p className="small-text">
          Minimum cashout: ${activeProps?.minimumRequired}
        </p>
      </div>

      {/* Amount */}
      <label>
        Cashout Amount <span className="required-star">*</span>
        </label>
      <input
        type="text"
        inputMode="decimal"
        name="amount"
        className={`input ${errors.amount ? "input-error" : ""}`}
        value={cashoutData.amount}
        onChange={handleInputChange}
        placeholder="$0.00"
      />
      {errors.amount && <p className="error-text">{errors.amount}</p>}
      <hr></hr>

      {/* Payout Method */}
      <label>Select Payout Method <span className="required-star">*</span></label>
      <div className="radio-row">
        <label className="radio-option">
          <input
            type="radio"
            name="payoutMethod"
            value="bank"
            checked={cashoutData.payoutMethod === "Bank Transfer"}
            onChange={handleInputChange}
          />
          <span>Bank Transfer</span>
        </label>

        <label className="radio-option">
          <input
            type="radio"
            name="payoutMethod"
            value="paypal"
            checked={cashoutData.payoutMethod === "paypal"}
            onChange={handleInputChange}
          />
          <span>PayPal</span>
        </label>
      </div>
      {errors.payoutMethod && <p className="error-text">{errors.payoutMethod}</p>}

      {/* Account Holder */}
      <label>
        Account Holder Name <span className="required-star">*</span>
        </label>
      <input
        type="text"
        name="accountHolder"
        className={`input ${errors.accountHolder ? "input-error" : ""}`}
        value={cashoutData.accountHolder}
        onChange={handleInputChange}
        placeholder="John Doe"
      />
      {errors.accountHolder && <p className="error-text">{errors.accountHolder}</p>}

      {/* ✅ Account Number + Routing Side-by-Side */}
      <div className="two-col">
        <div className="form-group">
          <label>
            Account Number <span className="required-star">*</span>
            </label>
          <input
            type="text"
            name="accountNumber"
            inputMode="numeric"
            className={`input ${errors.accountNumber ? "input-error" : ""}`}
            value={cashoutData.accountNumber}
            onChange={handleInputChange}
            placeholder="124583697"
            maxLength={NUMBER_RULES.accountNumber.max}
          />
          {errors.accountNumber && <p className="error-text">{errors.accountNumber}</p>}
        </div>

        <div className="form-group">
          <label>
            Routing Number <span className="required-star">*</span>
            </label>
          <input
            type="text"
            name="routingNumber"
            inputMode="numeric"
            className={`input ${errors.routingNumber ? "input-error" : ""}`}
            value={cashoutData.routingNumber}
            onChange={handleInputChange}
            placeholder="124583697"
            maxLength={NUMBER_RULES.routingNumber.len}
          />
          {errors.routingNumber && <p className="error-text">{errors.routingNumber}</p>}
        </div>
      </div>

      {/* Bank Name */}
      <label>
        Bank Name <span className="required-star">*</span>
        </label>
      <input
        type="text"
        name="bankName"
        className={`input ${errors.bankName ? "input-error" : ""}`}
        value={cashoutData.bankName}
        onChange={handleInputChange}
        placeholder="Chase Bank"
      />
      {errors.bankName && <p className="error-text">{errors.bankName}</p>}

      <div className="cashout-note">
        <strong>Note:</strong> All Cashout requests are reviewed by the GeoParking finance team for security purposes.You’ll receive aconfirmation email once your request is approved and processed.
      </div>

      <div className="modal-actions">
        <button
  className="cancel-btn"
  onClick={() => {
    resetCashoutForm();
    setShowCashoutModal(false);
  }}
>
  Cancel
</button>
        <button className="submit-btn" onClick={handleSubmitCashout}>
          Submit Request
        </button>
      </div>

    </div>
  </div>
)}
    </>
  );
};

export default Earnings;
